import React, { useState, useMemo, useRef, useEffect } from 'react';
import { format } from 'date-fns';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Checkbox } from '../../components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Plus, Trash2, Edit2, MapPin, ArrowLeftRight, UserCog, Sparkles, AlertCircle, UserPlus } from 'lucide-react';
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
  DragOverlay, useDroppable, useDraggable
} from '@dnd-kit/core';
import { StaffCountBadge } from './constants';

/* -------------------- Swap Popover -------------------- */
function SwapPopover({ shift, groups, onSwap }) {
  const [open, setOpen] = useState(false);
  // All target groups (exclude source group)
  const targets = groups.filter(g => g.number !== shift.group_number);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="text-[#6B3FA0] hover:text-white hover:bg-[#6B3FA0] shrink-0 p-1 rounded"
          title="Swap to another group"
          onClick={(e) => e.stopPropagation()}
          data-testid={`swap-shift-${shift.id}`}
        >
          <ArrowLeftRight className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-52 p-2" align="end" onClick={(e) => e.stopPropagation()}>
        <p className="text-[10px] font-bold uppercase tracking-wider text-[#8E85A0] mb-1.5">Swap to Group</p>
        <div className="max-h-56 overflow-y-auto space-y-0.5">
          {targets.map(g => {
            const occupant = g.shifts[0];
            return (
              <button
                key={g.number}
                onClick={() => { onSwap(shift, occupant, shift.group_number, g.number); setOpen(false); }}
                className="w-full text-left px-2 py-1.5 rounded text-xs hover:bg-[#EDE7F6] flex items-center justify-between gap-1"
                data-testid={`swap-target-${shift.id}-${g.number}`}
              >
                <span className="font-semibold text-[#2D1B4E]">Group {g.number}</span>
                <span className="text-[10px] text-[#8E85A0] truncate max-w-[110px]">
                  {occupant ? `↔ ${occupant.employee_name || '(open)'}` : '→ (open)'}
                </span>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/* -------------------- Replace Staff Popover (availability-aware) -------------------- */
function ReplacePopover({ shift, onReplace, availabilityFetcher }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [confirmSchedule, setConfirmSchedule] = useState(null); // suggestion object when confirm needed

  // Fetch fresh availability when popover opens or query changes.
  useEffect(() => {
    let ignore = false;
    if (!open) return;
    setLoading(true);
    (async () => {
      const data = await availabilityFetcher({ shift, q });
      if (!ignore) {
        setSuggestions(data || []);
        setLoading(false);
      }
    })();
    return () => { ignore = true; };
  }, [q, open, availabilityFetcher, shift]);

  const pick = (s) => {
    // Warn on hard conflicts (already scheduled at this time on this date).
    if (s.already_scheduled) {
      setConfirmSchedule(s);
      return;
    }
    onReplace(shift, s.id);
    setOpen(false); setQ(''); setSuggestions([]);
  };

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setQ(''); setSuggestions([]); setConfirmSchedule(null); } }}>
      <PopoverTrigger asChild>
        <button
          className="text-[#6B3FA0] hover:text-white hover:bg-[#6B3FA0] shrink-0 p-1 rounded"
          title="Replace staff — see who's available"
          onClick={(e) => e.stopPropagation()}
          data-testid={`replace-shift-${shift.id}`}
        >
          <UserCog className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-2" align="end" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1.5">
          <p className="text-[10px] font-bold uppercase tracking-wider text-[#8E85A0]">Who&apos;s available?</p>
          {suggestions.length > 0 && (
            <span className="text-[9px] text-[#8E85A0]">{suggestions.filter(s => !s.already_scheduled && !s.at_cap && s.available_today).length} free</span>
          )}
        </div>
        <Input
          autoFocus placeholder="Filter by name…" value={q}
          onChange={(e) => setQ(e.target.value)}
          className="h-7 text-xs border-[#9B7ED8] mb-1.5"
          data-testid={`replace-search-${shift.id}`}
        />
        {confirmSchedule && (
          <div className="mb-2 p-2 rounded border border-[#D9534F] bg-red-50" data-testid="replace-conflict-warning">
            <p className="text-xs font-semibold text-[#D9534F] mb-1">
              {confirmSchedule.name} is already scheduled at this time.
            </p>
            <p className="text-[10px] text-[#5A4D6E] mb-1.5">Assigning them here will create a double-booking.</p>
            <div className="flex gap-1">
              <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={() => setConfirmSchedule(null)}>Cancel</Button>
              <Button size="sm" className="h-6 text-[10px] bg-[#D9534F] hover:bg-[#B52B27] text-white"
                onClick={() => { onReplace(shift, confirmSchedule.id); setOpen(false); setQ(''); setSuggestions([]); setConfirmSchedule(null); }}
                data-testid="replace-confirm-conflict">
                Assign anyway
              </Button>
            </div>
          </div>
        )}
        <div className="max-h-64 overflow-y-auto space-y-0.5">
          {loading && <p className="text-[10px] text-[#8E85A0] px-2 py-1">Loading availability…</p>}
          {!loading && suggestions.length === 0 && (
            <p className="text-[10px] text-[#8E85A0] px-2 py-1">No matching staff</p>
          )}
          {!loading && suggestions.map(s => {
            const isCurrent = s.id === shift.employee_id;
            const isFree = !s.already_scheduled && !s.at_cap && s.available_today;
            const badge = s.already_scheduled ? { label: 'Conflict', cls: 'bg-red-100 text-[#D9534F] border-red-300' }
              : !s.available_today ? { label: 'Off today', cls: 'bg-orange-100 text-[#E65100] border-orange-300' }
              : s.at_cap ? { label: 'At cap', cls: 'bg-orange-100 text-[#E65100] border-orange-300' }
              : s.near_cap ? { label: 'Near cap', cls: 'bg-yellow-50 text-[#B45309] border-yellow-300' }
              : { label: 'Available', cls: 'bg-green-50 text-[#2E7D32] border-green-300' };
            return (
              <button
                key={s.id}
                disabled={isCurrent}
                onClick={() => pick(s)}
                className={`w-full text-left px-2 py-1.5 rounded text-xs border transition-colors ${
                  isCurrent ? 'bg-gray-50 border-gray-200 text-[#8E85A0] cursor-not-allowed' :
                  isFree ? 'bg-white border-transparent hover:bg-[#EDE7F6] hover:border-[#9B7ED8]' :
                  'bg-white border-transparent hover:bg-red-50 hover:border-red-200'
                }`}
                data-testid={`replace-pick-${shift.id}-${s.id}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold text-[#2D1B4E] truncate flex-1">
                    {s.name}
                    {isCurrent && <span className="ml-1 text-[9px] font-normal text-[#8E85A0]">(current)</span>}
                  </span>
                  <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border shrink-0 ${badge.cls}`}>{badge.label}</span>
                </div>
                <div className="flex items-center justify-between mt-0.5 text-[10px] text-[#8E85A0]">
                  <span>{s.weekly_hours}h{s.draft_hours > 0 ? ` (+${s.draft_hours}h draft)` : ''} / {s.max_weekly_hours}h this week</span>
                  <span className={s.remaining < 0 ? 'text-[#D9534F] font-semibold' : ''}>
                    {s.remaining >= 0 ? `${s.remaining}h left` : `${Math.abs(s.remaining)}h over`}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/* -------------------- Draggable Shift Card -------------------- */
function DraggableShiftCard({ shift, isSelected, onToggleSelect, onEdit, onSwap, onReplace, availabilityFetcher, groups }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: shift.id,
    data: { shift, groupNumber: shift.group_number }
  });
  const style = transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`, zIndex: 50, opacity: isDragging ? 0.5 : 1 } : undefined;
  const hasStaff = !!shift.employee_name;

  return (
    <div ref={setNodeRef} style={style} className={`group/card rounded-md p-2 mb-1.5 last:mb-0 border ${shift.is_draft ? 'bg-[#FEF6F0] border-dashed border-[#F4A261]' : 'bg-white/60 border-[#DED8EB]'}`} data-testid={`shift-card-${shift.id}`}>
      <div className="flex items-start justify-between gap-1 mb-0.5">
        <div className="flex items-center gap-1 min-w-0 flex-1 cursor-grab active:cursor-grabbing" {...listeners} {...attributes}>
          {onToggleSelect && (
            <Checkbox checked={isSelected} onCheckedChange={onToggleSelect} onClick={(e) => e.stopPropagation()} className="h-3 w-3 shrink-0" data-testid={`select-shift-${shift.id}`} />
          )}
          <p className="text-sm font-semibold text-[#2D1B4E] truncate">{shift.employee_name || <span className="italic text-[#8E85A0]">(open — no staff)</span>}</p>
          {shift.is_draft && <span className="text-[8px] font-bold uppercase tracking-wider text-[#E65100] bg-[#FFF3E0] px-1 py-0.5 rounded shrink-0" data-testid={`draft-badge-${shift.id}`}>Draft</span>}
        </div>
        {/* Action icons — always visible, more prominent on hover */}
        <div className="flex items-center gap-0.5 shrink-0 opacity-80 group-hover/card:opacity-100 transition-opacity">
          {onSwap && (
            <SwapPopover shift={shift} groups={groups} onSwap={onSwap} />
          )}
          {onReplace && hasStaff && availabilityFetcher && (
            <ReplacePopover shift={shift} onReplace={onReplace} availabilityFetcher={availabilityFetcher} />
          )}
          {onEdit && (
            <button onClick={(e) => { e.stopPropagation(); onEdit(shift); }} className="text-[#6B3FA0] hover:text-white hover:bg-[#6B3FA0] shrink-0 p-1 rounded" title="Edit shift" data-testid={`edit-shift-${shift.id}`}>
              <Edit2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>
      {(shift.participants || []).map((p, i) => <p key={`${p}-${i}`} className="text-xs text-[#5A4D6E] truncate pl-4">{p}</p>)}
      {shift.location && <p className="text-[10px] text-[#8E85A0] mt-0.5 pl-4 flex items-center gap-0.5"><MapPin className="h-2.5 w-2.5" />{shift.location}</p>}
    </div>
  );
}

/* -------------------- Droppable Group -------------------- */
function DroppableGroup({ number, shifts: groupShifts, onAdd, onEdit, isOver, selectedIds, onToggleSelect, onQuickAdd, onSwap, onReplace, availabilityFetcher, groups, highlighted, registerRef }) {
  const { setNodeRef } = useDroppable({ id: `group-${number}` });
  const [quickMode, setQuickMode] = useState(false);
  const [quickSearch, setQuickSearch] = useState('');
  const [quickResults, setQuickResults] = useState([]);
  const localRef = useRef(null);
  const hasShifts = (groupShifts || []).length > 0;

  // Register outer node for scrollIntoView
  useEffect(() => {
    if (registerRef) registerRef(number, localRef.current);
  }, [number, registerRef]);

  // Allow parent to open quick-add mode programmatically
  useEffect(() => {
    if (highlighted?.openQuick) setQuickMode(true);
  }, [highlighted]);

  return (
    <div ref={localRef}>
      <Card ref={setNodeRef}
        className={`border shadow-sm min-h-[100px] transition-all ${
          isOver ? 'border-[#6B3FA0] bg-[#EDE7F6] ring-2 ring-[#6B3FA0]/30 scale-[1.02]' :
          highlighted?.flash ? 'border-[#F4A261] bg-[#FEF6F0] ring-2 ring-[#F4A261]/50' :
          hasShifts ? 'border-[#9B7ED8] bg-[#EDE7F6]/50 hover:-translate-y-px' :
          'border-dashed border-[#DED8EB] bg-white'
        }`}
        data-testid={`group-${number}`}>
        <CardContent className="p-3">
          <div className="flex items-center justify-between mb-1.5">
            <Badge className={`text-[10px] ${hasShifts ? 'bg-[#6B3FA0]' : 'bg-[#F4A261]'} text-white`}>
              Group {number}{hasShifts && groupShifts.length > 1 ? ` · ${groupShifts.length}` : ''}
              {!hasShifts && ' · Open'}
            </Badge>
            <button
              onClick={(e) => { e.stopPropagation(); onAdd(number); }}
              className="text-[#8E85A0] hover:text-[#6B3FA0]"
              title="Add another staff to this group"
              data-testid={`add-to-group-${number}`}
            >
              <Plus className="h-3 w-3" />
            </button>
          </div>
          {hasShifts ? (
            <div data-testid={`group-${number}-shifts`}>
              {groupShifts.map(s => (
                <DraggableShiftCard
                  key={s.id}
                  shift={s}
                  isSelected={selectedIds?.has(s.id) || false}
                  onToggleSelect={onToggleSelect ? () => onToggleSelect(s.id) : null}
                  onEdit={onEdit}
                  onSwap={onSwap}
                  onReplace={onReplace}
                  availabilityFetcher={availabilityFetcher}
                  groups={groups}
                />
              ))}
            </div>
          ) : quickMode ? (
            <div className="space-y-1.5" data-testid={`quick-add-${number}`}>
              <Input
                autoFocus placeholder="Type staff name..."
                value={quickSearch}
                onChange={async (e) => {
                  setQuickSearch(e.target.value);
                  if (e.target.value.length >= 1 && onQuickAdd) {
                    const results = await onQuickAdd.search(e.target.value);
                    setQuickResults(results);
                  } else { setQuickResults([]); }
                }}
                onKeyDown={(e) => { if (e.key === 'Escape') { setQuickMode(false); setQuickSearch(''); setQuickResults([]); } }}
                className="h-7 text-xs border-[#9B7ED8] bg-white"
              />
              {quickResults.length > 0 && (
                <div className="space-y-0.5 max-h-24 overflow-y-auto">
                  {quickResults.map(s => (
                    <button key={s.id} onClick={() => { onQuickAdd.assign(s.id, number); setQuickMode(false); setQuickSearch(''); setQuickResults([]); }}
                      className="w-full text-left px-2 py-1 rounded text-xs text-[#2D1B4E] hover:bg-[#EDE7F6] truncate" data-testid={`quick-pick-${s.id}`}>
                      {s.name}
                    </button>
                  ))}
                </div>
              )}
              <button onClick={() => { setQuickMode(false); setQuickSearch(''); setQuickResults([]); }} className="text-[10px] text-[#8E85A0] hover:text-[#D9534F]">Cancel</button>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-1">
              <button className="w-full text-center py-2 cursor-pointer rounded-md hover:bg-[#EDE7F6] transition-colors group/add" onClick={() => setQuickMode(true)} data-testid={`quick-add-trigger-${number}`}>
                <div className="w-8 h-8 mx-auto rounded-full border-2 border-dashed border-[#9B7ED8] flex items-center justify-center group-hover/add:border-[#6B3FA0] group-hover/add:bg-[#6B3FA0] transition-colors">
                  <Plus className="h-4 w-4 text-[#9B7ED8] group-hover/add:text-white transition-colors" />
                </div>
                <p className="text-xs text-[#8E85A0] mt-1 font-medium group-hover/add:text-[#6B3FA0]">Fill Open Shift</p>
              </button>
              <button onClick={() => onAdd(number)} className="text-[10px] text-[#9B7ED8] hover:text-[#6B3FA0] underline" data-testid={`full-add-${number}`}>Full form</button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/* -------------------- Coverage Panel (Open Shifts + Fill All) -------------------- */
function CoveragePanel({ groups, onFocusGroup, onAutoFillAll, autoFilling }) {
  const openGroups = groups.filter(g => g.shifts.length === 0);
  const filledCount = groups.length - openGroups.length;
  const total = groups.length;
  if (total === 0) return null;

  return (
    <div
      className={`mb-3 rounded-lg border p-3 flex flex-wrap items-center justify-between gap-3 ${
        openGroups.length > 0 ? 'border-[#F4A261] bg-[#FEF6F0]' : 'border-[#81C784] bg-[#E8F5E9]'
      }`}
      data-testid="coverage-panel"
    >
      <div className="flex items-center gap-3">
        {openGroups.length > 0 ? (
          <AlertCircle className="h-5 w-5 text-[#E65100] shrink-0" />
        ) : (
          <Sparkles className="h-5 w-5 text-[#2E7D32] shrink-0" />
        )}
        <div>
          <p className={`text-sm font-bold ${openGroups.length > 0 ? 'text-[#E65100]' : 'text-[#2E7D32]'}`} data-testid="coverage-status">
            {openGroups.length > 0
              ? `${openGroups.length} open shift${openGroups.length > 1 ? 's' : ''} need coverage`
              : 'All shifts covered'}
          </p>
          <p className="text-[11px] text-[#5A4D6E]">
            {filledCount}/{total} groups filled
            {openGroups.length > 0 && ' — click a chip to fill, or auto-fill all.'}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        {openGroups.map(g => (
          <button
            key={g.number}
            onClick={() => onFocusGroup(g.number)}
            className="text-[11px] font-semibold px-2 py-1 rounded-full bg-white border border-[#F4A261] text-[#E65100] hover:bg-[#F4A261] hover:text-white transition-colors flex items-center gap-1"
            title={`Fill Group ${g.number}`}
            data-testid={`open-chip-${g.number}`}
          >
            <UserPlus className="h-3 w-3" />
            Group {g.number}
          </button>
        ))}
        {openGroups.length > 0 && onAutoFillAll && (
          <Button
            size="sm"
            onClick={onAutoFillAll}
            disabled={autoFilling}
            className="bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white gap-1 h-7 text-xs"
            data-testid="coverage-autofill-btn"
          >
            <Sparkles className="h-3 w-3" />
            {autoFilling ? 'Filling…' : 'Auto-Fill All Open'}
          </Button>
        )}
      </div>
    </div>
  );
}

/* -------------------- Main Day Program View -------------------- */
export default function DayProgramView({ shifts, date, onAdd, onEdit, onSwapGroups, onBatchDelete, onQuickAdd, onReplaceStaff, onAutoFillDay, autoFilling, availabilityFetcher, slotsNeeded = 8, onAddGroup }) {
  const [activeId, setActiveId] = useState(null);
  const [overGroupId, setOverGroupId] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [highlightMap, setHighlightMap] = useState({}); // { [groupNumber]: {flash, openQuick} }
  const groupRefs = useRef({});
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const groupCount = useMemo(() => {
    const maxUsed = shifts.reduce((m, s) => Math.max(m, s.group_number || 0), 0);
    return Math.max(slotsNeeded, maxUsed);
  }, [shifts, slotsNeeded]);
  const groups = useMemo(
    () => Array.from({ length: groupCount }, (_, i) => ({
      number: i + 1,
      shifts: shifts.filter(s => s.group_number === i + 1),
    })),
    [shifts, groupCount]
  );
  const activeShift = activeId ? shifts.find(s => s.id === activeId) : null;
  const nextEmptyGroup = groups.find(g => g.shifts.length === 0)?.number || null;
  const allFilledShiftIds = useMemo(
    () => groups.flatMap(g => g.shifts.map(s => s.id)),
    [groups]
  );

  const registerRef = (num, el) => { groupRefs.current[num] = el; };

  const focusGroup = (num) => {
    const el = groupRefs.current[num];
    if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setHighlightMap(prev => ({ ...prev, [num]: { flash: true, openQuick: true, key: Date.now() } }));
    setTimeout(() => {
      setHighlightMap(prev => {
        const next = { ...prev };
        if (next[num]) next[num] = { ...next[num], flash: false };
        return next;
      });
    }, 1400);
  };

  const toggleSelect = (shiftId) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(shiftId)) next.delete(shiftId); else next.add(shiftId);
      return next;
    });
  };
  const selectAll = () => {
    if (selected.size === allFilledShiftIds.length) setSelected(new Set());
    else setSelected(new Set(allFilledShiftIds));
  };
  const handleBatchDelete = () => {
    if (selected.size === 0) return;
    onBatchDelete([...selected]);
    setSelected(new Set());
  };

  const handleDragEnd = (event) => {
    setActiveId(null);
    setOverGroupId(null);
    const { active, over } = event;
    if (!over || !active) return;
    const sourceShift = active.data?.current?.shift;
    const targetGroupNum = parseInt(String(over.id).replace('group-', ''));
    if (!sourceShift || isNaN(targetGroupNum) || sourceShift.group_number === targetGroupNum) return;
    const targetShift = shifts.find(s => s.group_number === targetGroupNum);
    onSwapGroups(sourceShift, targetShift, sourceShift.group_number, targetGroupNum);
  };

  return (
    <div data-testid={`day-program-${date}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <p className="text-xs font-semibold text-[#8E85A0] uppercase tracking-wider">
            {format(new Date(date + 'T12:00:00'), 'EEEE, MMM d')} — 9:00 AM - 4:00 PM
          </p>
          <span className="text-[10px] text-[#9B7ED8] border border-[#DED8EB] px-2 py-0.5 rounded-full">Drag to swap · icons on cards for quick swap/replace</span>
        </div>
        <div className="flex items-center gap-3">
          <StaffCountBadge count={new Set(shifts.filter(s => s.group_number).map(s => s.group_number)).size} />
          {allFilledShiftIds.length > 0 && (
            <Button size="sm" variant="outline" onClick={selectAll}
              className="border-[#DED8EB] text-[#5A4D6E] hover:bg-[#EDE7F6] gap-1 h-7 text-xs" data-testid="select-all-btn">
              {selected.size === allFilledShiftIds.length ? 'Deselect All' : 'Select All'}
            </Button>
          )}
          {selected.size > 0 && (
            <Button size="sm" variant="outline" onClick={handleBatchDelete}
              className="border-[#D9534F] text-[#D9534F] hover:bg-red-50 gap-1 h-7 text-xs" data-testid="batch-delete-btn">
              <Trash2 className="h-3 w-3" /> Delete ({selected.size})
            </Button>
          )}
          {nextEmptyGroup && groupCount > 0 && (
            <Button size="sm" onClick={() => onAdd(nextEmptyGroup)} className="bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white gap-1 h-7 text-xs" data-testid="day-program-add-shift-btn">
              <Plus className="h-3 w-3" /> Add Shift
            </Button>
          )}
        </div>
      </div>

      {/* Coverage Panel */}
      {groupCount > 0 && (
        <CoveragePanel
          groups={groups}
          onFocusGroup={focusGroup}
          onAutoFillAll={onAutoFillDay}
          autoFilling={autoFilling}
        />
      )}

      <DndContext sensors={sensors} collisionDetection={closestCenter}
        onDragStart={(e) => setActiveId(e.active.id)}
        onDragOver={(e) => setOverGroupId(e.over?.id || null)}
        onDragEnd={handleDragEnd}>
        {groupCount === 0 ? (
          <div className="text-center py-10 text-[#8E85A0] border border-dashed border-[#DED8EB] rounded-lg bg-[#F7F4EF]" data-testid="day-program-closed">
            <p className="text-sm font-semibold text-[#5A4D6E]">No Explore Program groups scheduled for this day.</p>
            <p className="text-xs mt-1">Weekend / closed day. Admins can override via &quot;Shifts needed&quot; above.</p>
            {onAddGroup && (
              <Button size="sm" onClick={onAddGroup} className="mt-3 bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white gap-1 h-7 text-xs" data-testid="add-group-closed-btn">
                <Plus className="h-3 w-3" /> Open this day — Add Group 1
              </Button>
            )}
          </div>
        ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {groups.map(({ number, shifts: gShifts }) => (
            <DroppableGroup key={number} number={number} shifts={gShifts} onAdd={onAdd} onEdit={onEdit}
              isOver={overGroupId === `group-${number}`}
              selectedIds={selected}
              onToggleSelect={toggleSelect}
              onQuickAdd={onQuickAdd}
              onSwap={onSwapGroups}
              onReplace={onReplaceStaff}
              availabilityFetcher={availabilityFetcher}
              groups={groups}
              highlighted={highlightMap[number]}
              registerRef={registerRef} />
          ))}
          {onAddGroup && (
            <button onClick={onAddGroup}
              className="rounded-lg border-2 border-dashed border-[#DED8EB] hover:border-[#9B7ED8] hover:bg-[#F7F4FB] transition-colors flex flex-col items-center justify-center py-6 text-[#8E85A0] hover:text-[#6B3FA0] min-h-[120px]"
              data-testid="add-group-btn" title={`Add Group ${groupCount + 1} to this day`}>
              <Plus className="h-6 w-6 mb-1" />
              <span className="text-xs font-bold">Add Group {groupCount + 1}</span>
            </button>
          )}
        </div>
        )}
        <DragOverlay>
          {activeShift ? (
            <Card className="border-[#6B3FA0] bg-[#EDE7F6] shadow-lg w-[200px] opacity-90">
              <CardContent className="p-3">
                <Badge className="text-[10px] bg-[#6B3FA0] text-white mb-1">Moving</Badge>
                <p className="text-sm font-semibold text-[#2D1B4E] truncate">{activeShift.employee_name}</p>
                {(activeShift.participants || []).map((p, i) => <p key={`drag-${p}-${i}`} className="text-xs text-[#5A4D6E] truncate">{p}</p>)}
              </CardContent>
            </Card>
          ) : null}
        </DragOverlay>
      </DndContext>
    </div>
  );
}
